// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
} from '@stencil/core'

import type { Attributes } from '../../helpers'
import { inheritAriaAttributes, invariant } from '../../helpers'
import type { Tab } from '../tab/tab'

export type Layer = 'accessory' | 'main' | 'panel' | 'popup'
export type Level = 'root' | 'section' | 'subsection'
export type Orientation = 'horizontal' | 'vertical'

const HORIZONTAL_MOVE_KEYS: ReadonlySet<string> = new Set([
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
])

const VERTICAL_MOVE_KEYS: ReadonlySet<string> = new Set([
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
])

const TAB_SETTINGS: Record<
  string,
  Pick<Tab, 'importance' | 'indicatorPosition' | 'size'> | undefined
> = {
  'main-root': {
    importance: 'standard',
    size: 'large',
    indicatorPosition: 'block-end',
  },
  'main-section': {
    importance: 'standard',
    size: 'standard',
    indicatorPosition: 'block-end',
  },
  'popup-root': {
    importance: 'standard',
    size: 'standard',
    indicatorPosition: 'block-end',
  },
  'main-subsection': {
    importance: 'subdued',
    size: 'small',
    indicatorPosition: 'block-start',
  },
  'popup-section': {
    importance: 'subdued',
    size: 'small',
    indicatorPosition: 'inline-start',
  },
  'panel-root': {
    importance: 'standard',
    size: 'small',
    indicatorPosition: 'block-start',
  },
  'accessory-root': {
    importance: 'subdued',
    size: 'small',
    indicatorPosition: 'block-end',
  },
}

const isEnabledTabItem = (item: unknown): item is HTMLQdsTabElement => {
  if (!(item instanceof HTMLElement)) return false
  const tagName = item.tagName.toLowerCase()

  return (
    (!item.inert && tagName === 'qds-tab') ||
    (item as HTMLQdsTabElement).disabled !== true
  )
}

/**
 * `<qds-tabbar>` is the component that facilitates navigation between the groups
 * of related content within the page.
 * It allows users to switch between groups by clicking on Tabs without going to another page.
 */
@Component({
  tag: 'qds-tabbar',
  shadow: { delegatesFocus: true },
  styleUrl: 'tabbar.css',
})
export class Tabbar implements ComponentInterface {
  /**
   * The tabbar's layer.
   */
  @Prop() public layer?: Layer = 'main'

  /**
   * The tabbar's level.
   */
  @Prop() public level?: Level = 'root'

  /**
   * The tabbar's orientation.
   */
  @Prop() public orientation?: Orientation = 'horizontal'

  /**
   * the tablist Description
   */
  @Prop() public readonly tablistDescription?: string

  /**
   * Emitted when the user selects the tab.
   */
  @Event({ eventName: 'qdsTabShow', cancelable: false })
  private readonly showEmitter!: EventEmitter<HTMLElement>

  /**
   * Emitted when the user selects the tab.
   */
  @Event({ eventName: 'qdsTabHide', cancelable: false })
  private readonly hideEmitter!: EventEmitter<HTMLElement>

  @Element() private readonly host!: HTMLElement

  @State() private disableEndArrow = false

  @State() private disableStartArrow = false

  @State() private activeTab?: HTMLQdsTabElement

  #ro: ResizeObserver | undefined

  #tabbar?: HTMLElement

  #inheritedAttributes: Attributes = {}

  @Listen('resize', { target: 'window' })
  protected windowResize(): void {
    this.#onViewportCrop()
  }

  public componentWillLoad(): void {
    this.#inheritedAttributes = inheritAriaAttributes(this.host)

    const tabs = [
      ...this.host.querySelectorAll('qds-tab'),
    ] as HTMLQdsTabElement[]
    const preselectedTab = tabs.find((tab) => tab.selected)
    if (preselectedTab) this.#setActiveTab(preselectedTab)
    else if (tabs.length > 0) this.#setActiveTab(tabs[0])
  }

  public componentDidRender(): void {
    if (this.activeTab) this.#switchTabPanel(this.activeTab)

    const settings = TAB_SETTINGS[`${this.layer}-${this.level}`]
    if (!settings) return

    for (const tab of this.host.querySelectorAll('qds-tab')) {
      /* eslint-disable @typescript-eslint/no-unsafe-member-access */
      tab.importance = settings.importance
      tab.size = settings.size
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      tab.indicatorPosition =
        this.orientation === 'vertical' ? 'inline-end' : tab.indicatorPosition
      /* eslint-enable @typescript-eslint/no-unsafe-member-access */
    }
  }

  public componentDidLoad(): void {
    invariant(this.#tabbar)

    this.#ro = new ResizeObserver(this.#onScroll)
    this.#ro.observe(this.#tabbar)
  }

  public disconnectedCallback(): void {
    if (this.#ro) this.#ro.disconnect()
  }

  public render() {
    return (
      <div
        class={{ 'qds-vertical-divider': this.orientation === 'vertical' }}
        data-layer={this.layer}
        data-level={this.level}
      >
        <div class="qds-tabbar">
          <div
            class={{
              'qds-flex': !(this.disableEndArrow && this.disableStartArrow),
              'qds-hidden':
                (this.disableEndArrow && this.disableStartArrow) ||
                this.orientation === 'vertical',
            }}
          >
            <qds-button
              text="show previous tab"
              importance="subdued"
              icon-name="dropdown"
              icon-library="core"
              class="qds-arrow-start"
              onClick={this.#scrollToStart}
              disabled={this.disableStartArrow}
              iconOnly
            />
          </div>
          <ul
            class={{
              'qds-tabs': true,
              'qds-vertical': this.orientation === 'vertical',
            }}
            role="tablist"
            aria-label={this.tablistDescription}
            ref={(element) => {
              this.#tabbar = element
            }}
            onClick={this.#onClick}
            onKeyDown={this.#onKeyDown}
            onMouseDown={this.#onMouseDown}
            onScroll={this.#onScroll}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...this.#inheritedAttributes}
          >
            <slot onSlotchange={this.#onSlotchange} />
          </ul>
          <div
            class={{
              'qds-flex': !(this.disableEndArrow && this.disableStartArrow),
              'qds-hidden':
                (this.disableEndArrow && this.disableStartArrow) ||
                this.orientation === 'vertical',
            }}
          >
            <qds-button
              text="show next tab"
              icon-name="dropdown"
              icon-library="core"
              importance="subdued"
              class="qds-arrow-end"
              onClick={this.#scrollToEnd}
              disabled={this.disableEndArrow}
              iconOnly
            />
          </div>
        </div>
        {((this.level === 'root' &&
          (this.layer === 'popup' || this.layer === 'accessory')) ||
          (this.level === 'section' && this.layer === 'main')) && (
          <qds-divider vertical={this.orientation === 'vertical'} />
        )}
      </div>
    )
  }

  readonly #scrollToStart = (): void => {
    invariant(this.#tabbar)

    const isRTL = globalThis.getComputedStyle(this.host).direction === 'rtl'
    this.#tabbar.scroll({
      left: isRTL
        ? this.#tabbar.scrollLeft + this.#tabbar.clientWidth
        : this.#tabbar.scrollLeft - this.#tabbar.clientWidth,
    })
  }

  readonly #scrollToEnd = (): void => {
    invariant(this.#tabbar)

    const isRTL = globalThis.getComputedStyle(this.host).direction === 'rtl'
    this.#tabbar.scroll({
      left: isRTL
        ? this.#tabbar.scrollLeft - this.#tabbar.clientWidth
        : this.#tabbar.scrollLeft + this.#tabbar.clientWidth,
    })
  }

  #setActiveTab(tab: HTMLQdsTabElement): void {
    this.activeTab = tab

    for (const t of this.host.querySelectorAll('qds-tab')) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const target = t.shadowRoot?.querySelector('[role="tab"]')

      const isSelected = t === tab
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      t.selected = isSelected
      if (target instanceof globalThis.Element)
        target.ariaSelected = isSelected.toString()
    }
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/class-methods-use-this
  #getAllTabPanels(): HTMLElement[] {
    const tabPanels = document.querySelectorAll('[role="tabpanel"]')
    return [...tabPanels].filter(
      (element): element is HTMLElement => element instanceof HTMLElement,
    )
  }

  #switchTabPanel(activeTab: HTMLQdsTabElement): void {
    const target = activeTab.shadowRoot?.querySelector('[role="tab"]')
    const tabAriaControls = target?.getAttribute('aria-controls')
    const tabPanels = this.#getAllTabPanels()

    for (const panel of tabPanels)
      panel.hidden = tabAriaControls !== panel.getAttribute('id')
  }

  #handleTabActivation(tab: HTMLQdsTabElement): void {
    const previousTab = this.activeTab
    if (previousTab === tab) return

    this.activeTab = tab

    this.hideEmitter.emit(previousTab)

    this.showEmitter.emit(tab)
    this.#setCurrentTab(tab)
    this.#switchTabPanel(tab)
  }

  #getTabs(): HTMLQdsTabElement[] {
    const slot = this.host.shadowRoot?.querySelector('slot')
    return slot
      ? [...slot.assignedElements({ flatten: true })].filter<HTMLQdsTabElement>(
          (element) => isEnabledTabItem(element),
        )
      : []
  }

  #getCurrentTab(): HTMLQdsTabElement | undefined {
    const currentTab = this.#getTabs().find(
      (tab) =>
        tab.shadowRoot
          ?.querySelector('[role="tab"]')
          ?.getAttribute('tabindex') === '0',
    )
    return currentTab
  }

  #setCurrentTab(tab: HTMLQdsTabElement): void {
    const tabs = this.#getTabs()
    for (const t of tabs) {
      const isCurrent = t === tab
      const target = t.shadowRoot?.querySelector('[role="tab"]')
      const closeButton = t.shadowRoot?.querySelector('.qds-target')

      const isDisabled = target?.getAttribute('aria-disabled') === 'true'
      const shouldActivateTab = isCurrent && !isDisabled
      const tabindex = shouldActivateTab ? '0' : '-1'

      if (target) {
        target.setAttribute('tabindex', tabindex)
        if (shouldActivateTab) this.#handleTabActivation(t)
      }

      if (closeButton) closeButton.setAttribute('tabindex', tabindex)
    }
  }

  readonly #onClick = (event: MouseEvent): void => {
    const { target } = event
    if (!(target instanceof HTMLElement)) return
    const tab = target.closest('qds-tab')
    if (!tab || tab.disabled === true || tab.inert) return

    this.#handleTabActivation(tab)
    this.#setActiveTab(tab)
  }

  readonly #onKeyDown = (event: KeyboardEvent): void => {
    const isVertical = this.orientation === 'vertical'
    const MOVE_SELECTION_KEYS = isVertical
      ? VERTICAL_MOVE_KEYS
      : HORIZONTAL_MOVE_KEYS
    if (!MOVE_SELECTION_KEYS.has(event.key)) return

    const tabs = this.#getTabs()
    if (tabs.length === 0) return

    event.preventDefault()
    event.stopPropagation()

    const activeTab = this.#getCurrentTab()
    let index = activeTab ? tabs.indexOf(activeTab) : 0

    const isRTL = globalThis.getComputedStyle(this.host).direction === 'rtl'

    let nextTab: HTMLQdsTabElement | undefined
    do {
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp': {
          index = (isRTL ? index < tabs.length - 1 : index > 0)
            ? index - (isRTL ? -1 : 1)
            : isRTL
              ? 0
              : tabs.length - 1
          break
        }
        case 'ArrowRight':
        case 'ArrowDown': {
          index = (isRTL ? index > 0 : index < tabs.length - 1)
            ? index + (isRTL ? -1 : 1)
            : isRTL
              ? tabs.length - 1
              : 0
          break
        }
        case 'Home': {
          index = isRTL ? tabs.length - 1 : 0
          break
        }
        case 'End': {
          index = isRTL ? 0 : tabs.length - 1
          break
        }
        default:
      }
      nextTab = tabs[index]
    } while (nextTab.disabled === true)

    this.#setCurrentTab(nextTab)

    const target =
      nextTab.shadowRoot?.querySelector<HTMLElement>('[role="tab"]')
    if (target) {
      target.focus()
      target.scrollIntoView()
    }
    this.#handleTabActivation(nextTab)
    this.#setActiveTab(nextTab)
  }

  readonly #onMouseDown = (event: MouseEvent): void => {
    const { target } = event

    if (isEnabledTabItem(target)) this.#setCurrentTab(target)
  }

  readonly #onScroll = (): void => {
    if (!this.#tabbar) return

    const { clientWidth, scrollWidth, scrollLeft } = this.#tabbar

    const isRTL = globalThis.getComputedStyle(this.host).direction === 'rtl'
    this.disableStartArrow = scrollLeft === 0
    this.disableEndArrow = isRTL
      ? scrollWidth + scrollLeft <= clientWidth
      : scrollWidth - clientWidth - scrollLeft <= 1
  }

  readonly #onViewportCrop = (): void => {
    if (!this.#tabbar) return

    const { scrollWidth, clientWidth } = this.#tabbar

    if (scrollWidth > clientWidth) {
      this.#onScroll()
    }
  }

  readonly #onSlotchange = (): void => {
    const tabs = this.#getTabs()

    if (tabs.length > 0) this.#setCurrentTab(tabs[0])
  }
}
